import { useEffect, useMemo } from 'react';
import type { UseFormReset } from 'react-hook-form';
import { EpmVdpSourceType } from '@/common/graphql';
import type {
  FunctionalitySuggestion,
  ManagerialAccountingFormData,
  RoleTypeSuggestion,
  VddSuggestion,
  VdpSuggestion,
} from '@/common/types';

interface Props {
  companyId: string | null;
  companyName: string | null;
  vddId: string | null;
  vddName: string | null;
  functionalityId: string | null;
  functionalityName: string | null;
  roleTypeId: string | null;
  roleTypeName: string | null;
  reset: UseFormReset<ManagerialAccountingFormData>;
}

export const useManagerialAccountingDefaultSuggestion = ({
  companyId,
  companyName,
  vddId,
  vddName,
  functionalityId,
  functionalityName,
  roleTypeId,
  roleTypeName,
  reset,
}: Props) => {
  useEffect(() => {
    reset(prev => ({
      ...prev,
      vdp: companyId ? [{ sourceType: EpmVdpSourceType.Company, companyId: companyId, percentage: 100 }] : [],
      vdd: vddId ? [{ epmMasterVddId: vddId, percentage: 100 }] : [],
      functionality: functionalityId ? [{ epmMasterFunctionalityId: functionalityId, percentage: 100 }] : [],
      roleType: roleTypeId ? [{ epmMasterRoleTypeId: roleTypeId, percentage: 100 }] : [],
      allocationKey: [],
    }));
  }, [companyId, vddId, functionalityId, roleTypeId, reset]);

  const vdpSuggestion = useMemo(
    () =>
      companyId && companyName
        ? [{ companyId: companyId, companyName: companyName, percentage: 100, days: 0 } as VdpSuggestion]
        : [],
    [companyId, companyName],
  );

  const vddSuggestion = useMemo(
    () =>
      vddId && vddName
        ? [
            {
              suggestionSourceNames: [],
              epmMasterVddId: vddId,
              name: vddName,
              percentage: 100,
            } as VddSuggestion,
          ]
        : [],
    [vddId, vddName],
  );

  const functionalitySuggestion = useMemo(
    () =>
      functionalityId && functionalityName
        ? [
            {
              suggestionSourceNames: [],
              epmMasterFunctionalityId: functionalityId,
              name: functionalityName,
              percentage: 100,
            } as FunctionalitySuggestion,
          ]
        : [],
    [functionalityId, functionalityName],
  );

  const roleTypeSuggestion = useMemo(
    () =>
      roleTypeId && roleTypeName
        ? [
            {
              suggestionSourceNames: [],
              epmMasterRoleTypeId: roleTypeId,
              name: roleTypeName,
              percentage: 100,
            } as RoleTypeSuggestion,
          ]
        : [],
    [roleTypeId, roleTypeName],
  );

  return { vdpSuggestion, vddSuggestion, functionalitySuggestion, roleTypeSuggestion };
};
